import React, { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePrimaryColor,
  usePropertyAddress,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import {
  MultihubLogoMonoWhite,
  MultihubLogoMonoBlack,
  repliWhiteLogoUrl,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebook,
  faInstagram,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import {
  DAY_FULL_LABELS,
  DAY_SHORT_LABELS,
  isClosedDay,
  normalizeDay,
  withClosedDays,
  type OfficeHourEntry,
} from '../utils/officeHours';

/**
 * Via6 Footer override.
 *
 * Why we forked from the base rise Footer instead of style-wrapping it:
 * the Via6 brief reaches inside the parent's JSX tree in two ways the
 * style-wrapper pattern can't reach:
 *
 *   1. Phone + Address + Office Hours + Social icons collapse into a
 *      SINGLE inline row that sits next to the logo on desktop (the
 *      base footer renders Phone/Address/Hours as three separate
 *      columns and has no social block at all).
 *   2. Each social icon is wrapped in its own circular secondary-color
 *      border (one ring per icon, not a single shared pill).
 *
 * Everything outside those two changes — contrast-driven logo variant
 * selection, RentCafe IQ z-index pinning, legal nav, bottom credit
 * row — mirrors the base rise Footer one-for-one, since the editor
 * affordances and behavior there are already correct for Via6.
 *
 * Background treatment (photo + brand-tinted dark gradient + 10px
 * secondary stripe) is owned by this component directly now rather
 * than by an external wrapper, because the underlying `<footer>` is
 * ours and we can paint its background straight on without the inline
 * `background-color: var(--vibe-color-primary)` fight the wrapper was
 * resolving in the previous (wrapper) revision.
 *
 * Slot/token paths re-use the base footer's namespace (`footer.*`)
 * verbatim where the field is the same, so any editor content that
 * was authored against the previous wrapper layer survives this
 * restructure. New social link slot groups live under `footer.*` too
 * (e.g. `footer.facebook_link`) so all footer-scoped editing is
 * grouped together in the editor.
 */

// ============================================================================
// Contrast text color (mirrors `getContrastTextColor` from @vibesites/tokens —
// see base rise Footer for the rationale; kept identical so logo color
// (white vs black) stays in lockstep with --vibe-color-text-on-primary).
// ============================================================================

const TEXT_ON_PRIMARY_LIGHT = '#ffffff';
const TEXT_ON_PRIMARY_DARK = '#1a1a1a';

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '').trim();
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0] + cleaned[0], 16),
      g: parseInt(cleaned[1] + cleaned[1], 16),
      b: parseInt(cleaned[2] + cleaned[2], 16),
    };
  }
  if (cleaned.length === 6) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
}

function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function getContrastTextColor(bgHex: string | null | undefined): string {
  if (!bgHex) return TEXT_ON_PRIMARY_LIGHT;
  const luminance = getRelativeLuminance(bgHex);
  return luminance > 0.55 ? TEXT_ON_PRIMARY_DARK : TEXT_ON_PRIMARY_LIGHT;
}

// ============================================================================
// Local placeholder for the Repli "mono black" wordmark (the assets package
// only ships `repliWhiteLogoUrl`). We swap to the white URL by default on
// dark primary backgrounds — the black variant is only reached when the
// primary is a genuinely light pastel/cream, which is unusual for Via6 but
// kept here for parity with the base footer's contrast branch.
// ============================================================================

// Falls back to the white logo if the black variant isn't present in this
// theme's assets — Via6 currently doesn't ship a black wordmark, but the
// contrast branch needs a value to keep the conditional balanced.
const repliBlackLogoUrl = repliWhiteLogoUrl;

// ============================================================================
// Office-hours helpers (verbatim from base rise Footer — kept inline because
// the base file doesn't export them).
// ============================================================================

type OfficeHour = OfficeHourEntry;

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

interface GroupedHours {
  label: string;
  shortLabel: string;
  hours: string;
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Pad omitted weekdays as Closed so Sat/Sun (or any feed-skipped day)
  // still appear. withClosedDays also sorts Mon–Sun.
  const sorted = withClosedDays(raw);

  const groups: GroupedHours[] = [];

  for (const entry of sorted) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    const dayLabel = DAY_FULL_LABELS[key] || entry.day;
    const dayShortLabel = DAY_SHORT_LABELS[key] || (entry.day || '').slice(0, 3);
    const hoursStr = isClosedDay(entry)
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayLabel, shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ----------------------------------------------------------------------------
// The first eight defs mirror the base rise Footer one-for-one so editor
// content authored against the previous wrapper layer survives. The
// social-link defs (footer.facebook_link … footer.google_link) are new to
// this override — the base footer has no social row.
// ============================================================================

// Footer background photo — bound to the property gallery so every site
// gets its own imagery. Previously a Via6 Seattle Multihub URL was
// hard-coded into the footer's CSS and shipped to every descendant.
const bgImageDef = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Footer background',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const logoSlotDef = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const managedByLogoDef = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_mono_black_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Main Street, City, ST 00000' },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ---- Social links (new in this override) ----
// Scoped to Facebook / Instagram / YouTube only per Via6 brief — these are
// the three networks the property actively maintains. Adding more is a
// matter of cloning a slot def and dropping another icon into the row.
const facebookLinkDef = {
  label: 'Facebook',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url', suggestedFields: ['property_facebook_url'] },
    _label: { label: 'Aria Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef = {
  label: 'Instagram',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url', suggestedFields: ['property_instagram_url'] },
    _label: { label: 'Aria Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const youtubeLinkDef = {
  label: 'YouTube',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_youtube_url', suggestedFields: ['property_youtube_url'] },
    _label: { label: 'Aria Label', type: 'text', defaultValue: 'YouTube' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ---- Optional program logos ----
// Each slot supports two variants — `image` (light/white-on-dark) and
// `image_dark` (dark/black-on-light). The `resolveProgramLogo` helper
// picks which variant to render based on primary color luminance.
// Anchors only render when an image is uploaded; empty slots stay hidden.
const programLogoOneDef = {
  label: 'Program Logo 1',
  fields: {
    image: { label: 'Logo Image (Light)', type: 'image', defaultValue: '' },
    image_dark: { label: 'Logo Image (Dark)', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Program logo' },
    url: { label: 'URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const programLogoTwoDef = {
  label: 'Program Logo 2',
  fields: {
    image: { label: 'Logo Image (Light)', type: 'image', defaultValue: '' },
    image_dark: { label: 'Logo Image (Dark)', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Program logo' },
    url: { label: 'URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phone = useContentToken('footer.phone');

  // Same contrast branch as the base rise Footer: pick mono_white vs
  // mono_black logo defaults based on the primary color's luminance so the
  // foreground reads correctly without per-property overrides.
  const primaryColor = usePrimaryColor();
  const textOnPrimary = getContrastTextColor(primaryColor);
  const useWhiteOnPrimary = textOnPrimary === TEXT_ON_PRIMARY_LIGHT;

  const runtimeLogoSlotDef = useWhiteOnPrimary
    ? logoSlotDef
    : {
        ...logoSlotDef,
        fields: {
          ...logoSlotDef.fields,
          image: { ...logoSlotDef.fields.image, defaultField: 'property_mono_black_logo' },
        },
      };

  const runtimeManagedByLogoDef = useWhiteOnPrimary
    ? managedByLogoDef
    : {
        ...managedByLogoDef,
        fields: {
          ...managedByLogoDef.fields,
          image: { ...managedByLogoDef.fields.image, defaultField: 'property_company_mono_black_logo' },
        },
      };

  const logo = useSlotGroup('footer.logo', runtimeLogoSlotDef);
  const bgImage = useSlotGroup('footer.bg_image', bgImageDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', runtimeManagedByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  // Fallback maps URL when the editor hasn't set a custom address link.
  // The worktree's vendored runtime doesn't ship `usePropertyAddressUrl` (it
  // post-dates this fork), so we build a "Google Maps search" URL from the
  // raw address text. Mobile-safe: the `search?api=1&query=…` pattern is the
  // public Google Maps deep link spec and routes to the native app where
  // installed.
  const propertyAddressText = usePropertyAddress();
  const propertyAddressUrl = propertyAddressText
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(propertyAddressText)}`
    : '';
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);

  // Social links — new to this override; each icon renders only when its
  // URL is bound, so the column collapses gracefully on properties that
  // don't have all three networks.
  const facebook = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagram = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const youtube = useSlotGroup('footer.youtube_link', youtubeLinkDef);

  const hasAnySocial = !!(facebook.url || instagram.url || youtube.url);

  // ---- Optional program logos ----
  // Contrast pivot mirrors the rest of the footer: on dark primaries
  // (`useWhiteOnPrimary === true`) we prefer the light variant; on
  // light primaries we prefer the dark variant. If only one variant
  // is uploaded we fall back to it and apply a CSS filter so the
  // single asset still contrasts correctly. Each anchor only renders
  // when its resolved URL is non-empty, so empty slots leave no dead
  // links in the rendered footer.
  const programLogoOne = useSlotGroup('footer.program_logo_1', programLogoOneDef);
  const programLogoTwo = useSlotGroup('footer.program_logo_2', programLogoTwoDef);

  function resolveProgramLogo(slot: { image?: string; image_dark?: string }) {
    if (useWhiteOnPrimary) {
      if (slot.image) return { url: slot.image, filter: undefined as string | undefined };
      if (slot.image_dark) return { url: slot.image_dark, filter: 'brightness(0) invert(1)' };
      return { url: '', filter: undefined };
    }
    if (slot.image_dark) return { url: slot.image_dark, filter: undefined as string | undefined };
    if (slot.image) return { url: slot.image, filter: 'brightness(0)' };
    return { url: '', filter: undefined };
  }
  const programLogoOneRender = resolveProgramLogo(programLogoOne);
  const programLogoTwoRender = resolveProgramLogo(programLogoTwo);
  const hasAnyProgramLogo = !!(programLogoOneRender.url || programLogoTwoRender.url);

  // Shared social-icons trio. Rendered in two places: beneath the logo on
  // tablet (md), and inside the inline row on mobile + desktop. Pulled out
  // into a JSX const so the EditableSlotGroup wrappers (and therefore
  // editor affordances) follow whichever instance is visible at the
  // current breakpoint — see the two `{socialIconsRow}` call sites below.
  const socialIconsRow = hasAnySocial ? (
    <div className="flex items-center gap-1.5">
      {facebook.url && (
        <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
          <a
            href={facebook.url}
            target={facebook._target || '_blank'}
            rel="noopener noreferrer"
            aria-label={facebook._label || 'Facebook'}
            className="social-icon-ring"
          >
            <FontAwesomeIcon icon={faFacebook} className="w-3 h-3" />
          </a>
        </EditableSlotGroup>
      )}
      {instagram.url && (
        <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
          <a
            href={instagram.url}
            target={instagram._target || '_blank'}
            rel="noopener noreferrer"
            aria-label={instagram._label || 'Instagram'}
            className="social-icon-ring"
          >
            <FontAwesomeIcon icon={faInstagram} className="w-3 h-3" />
          </a>
        </EditableSlotGroup>
      )}
      {youtube.url && (
        <EditableSlotGroup groupPath="footer.youtube_link" definition={youtubeLinkDef}>
          <a
            href={youtube.url}
            target={youtube._target || '_blank'}
            rel="noopener noreferrer"
            aria-label={youtube._label || 'YouTube'}
            className="social-icon-ring"
          >
            <FontAwesomeIcon icon={faYoutube} className="w-3 h-3" />
          </a>
        </EditableSlotGroup>
      )}
    </div>
  ) : null;

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  // Yardi's webchat launcher sets its own inline z-index after hydration, so
  // we observe the DOM for the real launcher nodes and overwrite z-index with
  // `!important` at 120 — just above the open header. Scoped to Footer because
  // Footer is the only chrome component rendered on every page. We only touch
  // `.rcc-app-cta-wrap`, `.rcc-app-controls`, and `#conversations-webchat-toggle`
  // (no broad iframe / yardi / rentcafe text matches, no outermost-fixed walk).
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const LAUNCHER_SELECTOR =
      '.rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle';
    const Z = '120';

    function isLauncher(el: Element): boolean {
      return el.matches(LAUNCHER_SELECTOR);
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!isLauncher(el)) return;
      pin(el);
    }

    function scanSubtree(root: ParentNode) {
      const candidates = root.querySelectorAll(LAUNCHER_SELECTOR);
      for (const c of Array.from(candidates)) processCandidate(c);
      if (root instanceof Element) processCandidate(root);
    }

    scanSubtree(document.body);

    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id', 'class', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <footer
      className="text-primary-foreground"
      style={{
        position: 'relative',
        // Solid primary as the base fill. The gallery photo (if any)
        // and brand-tinted gradient sit as absolute layers above so
        // empty galleries still read as a clean brand footer.
        backgroundColor: 'var(--vibe-color-primary)',
        overflow: 'hidden',
      }}
    >
      {/* Background photo + brand-tinted dark gradient. Bound to the
          property gallery — no hardcoded Via6 Seattle CDN URL. Image
          visibility is gated by the gradient alphas (lower alpha =
          more photo, higher alpha = more brand fade). */}
      <EditableSlotGroup groupPath="footer.bg_image" definition={bgImageDef}>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        >
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt=""
              className="w-full h-full object-cover"
              sizes="100vw"
            />
          ) : null}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              // The gradient layer sits on top of the photo and controls
              // how prominent it reads. Higher primary-color percentages
              // + higher black alpha = more opaque overlay = more
              // subtle photo showing through. These values (50%/0.45 at
              // top, 65%/0.60 at bottom) give a noticeable but
              // understated texture rather than a dominant image.
              backgroundImage: `
                linear-gradient(
                  180deg,
                  color-mix(in srgb, var(--vibe-color-primary) 50%, rgba(0, 0, 0, 0.45)) 0%,
                  color-mix(in srgb, var(--vibe-color-primary) 65%, rgba(0, 0, 0, 0.60)) 100%
                )
              `,
            }}
          />
        </div>
      </EditableSlotGroup>

      {/* 10px secondary-color accent stripe across the very top of
          the footer. Decorative — `aria-hidden` because it has no
          semantic role. */}
      <div
        aria-hidden="true"
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          height: '10px',
          backgroundColor: 'var(--vibe-color-secondary)',
          display: 'block',
        }}
      />

      {/* ---- Main Footer Content: Logo + inline (Phone | Address | Hours) ---- */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6 md:pt-10 md:pb-8" style={{ position: 'relative', zIndex: 1 }}>
        <div className="flex flex-col md:flex-row md:items-center gap-8 md:gap-10">

          {/* Logo (+ tablet-only Socials beneath)
              On md the social icons drop into a second row inside this
              column so the inline strip of Hours/Address/Phone gets the
              full remaining width to breathe. On mobile (< md) and lg+
              the canonical socials block lives inside the inline row
              below, and this nested socials block is `hidden`.

              `items-center` (no `md:items-start` override) keeps the
              logo + socials centered horizontally within the column on
              tablet, so the narrower socials row sits centered under
              the logo. On lg+ the socials are hidden and the logo's
              bounding box drives the column width, so centering is
              visually equivalent to left-aligning. */}
          <div className="flex flex-col items-center md:flex-shrink-0 gap-4">
            <EditableSlotGroup groupPath="footer.logo" definition={runtimeLogoSlotDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    // Tablet shrinks the logo (h-14 / max-w-150px) so it
                    // doesn't dominate the column once socials are stacked
                    // beneath it. Mobile and lg+ both render at the
                    // canonical h-20 / max-w-190px.
                    className="h-20 md:h-14 lg:h-20 w-auto max-w-[190px] md:max-w-[150px] lg:max-w-[190px] object-contain"
                  />
                ) : (
                  <div className="font-heading text-2xl text-primary-foreground">Our Community</div>
                )}
              </a>
            </EditableSlotGroup>

            {hasAnyProgramLogo && (
              <div className="flex items-center justify-center gap-6">
                {programLogoOneRender.url && (
                  <EditableSlotGroup groupPath="footer.program_logo_1" definition={programLogoOneDef}>
                    <a
                      href={programLogoOne.url || '#'}
                      target={programLogoOne._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={programLogoOne.alt || 'Program logo'}
                      className="inline-flex transition-[filter] duration-200 hover:brightness-110"
                    >
                      <VibesitesImage
                        url={programLogoOneRender.url}
                        alt={programLogoOne.alt || 'Program logo'}
                        className="h-10 w-auto max-w-[120px] object-contain"
                        style={programLogoOneRender.filter ? { filter: programLogoOneRender.filter } : undefined}
                      />
                    </a>
                  </EditableSlotGroup>
                )}

                {programLogoTwoRender.url && (
                  <EditableSlotGroup groupPath="footer.program_logo_2" definition={programLogoTwoDef}>
                    <a
                      href={programLogoTwo.url || '#'}
                      target={programLogoTwo._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={programLogoTwo.alt || 'Program logo'}
                      className="inline-flex transition-[filter] duration-200 hover:brightness-110"
                    >
                      <VibesitesImage
                        url={programLogoTwoRender.url}
                        alt={programLogoTwo.alt || 'Program logo'}
                        className="h-10 w-auto max-w-[120px] object-contain"
                        style={programLogoTwoRender.filter ? { filter: programLogoTwoRender.filter } : undefined}
                      />
                    </a>
                  </EditableSlotGroup>
                )}
              </div>
            )}

            {hasAnySocial && (
              <div className="hidden md:flex lg:hidden">
                {socialIconsRow}
              </div>
            )}
          </div>

          {/* Single inline row of Phone | Address | Office Hours next to the
              logo. On mobile this wraps to stacked vertical columns; on
              md+ it lays out left-to-right with a flexible gap. Each block
              keeps its own caption label so the editor can still wire them
              individually.

              No vertical divider stripes between cells per Via6 brief —
              spacing alone (the `md:px-6` per-cell padding plus the
              flex-row gap) separates the blocks visually. */}
          {/* Tablet uses a lighter `md:px-2` per cell (vs `lg:px-6`) so
              the inline strip doesn't burn ~150px on internal padding at
              the narrower md width — that was forcing Address/Hours text
              to wrap aggressively. The inter-cell gap stays at `gap-4`
              across md+ so there's still a clear visual separator. */}
          <div className="flex-1 flex flex-col md:flex-row md:items-stretch gap-6 md:gap-4">
            {/* Office Hours — same grouping logic as the base footer, but
                rendered in a compact two-column dl so it fits in the inline
                row alongside Address and Phone. Day labels are always the
                3-letter short form (Mon / Tue / Wed …) per the Via6 brief,
                regardless of breakpoint, so the row reads consistently
                across viewport widths and the inline strip doesn't have to
                expand to fit "Wednesday" on wide displays. */}
            <div className="text-center md:text-left md:px-2 lg:px-6 lg:first:pl-0">
              <RichTextContent
                path="footer.office_hours_label"
                defaultValue="<p>Office hours</p>"
                as="p"
                className="font-body text-[15px] font-bold text-primary-foreground mb-1"
              />
              {groupedHours.length > 0 ? (
                <dl className="space-y-0.5 text-center md:text-left">
                  {groupedHours.map((group, index) => (
                    // On mobile, the day label and hours sit side-by-side
                    // centered as a pair (`justify-center`) so the row reads
                    // as a tidy, balanced unit beneath the centered label.
                    // On md+ the inline strip is narrower per cell, so we
                    // restore `justify-between` to keep the columns aligned
                    // within the cell's bounds.
                    <div key={index} className="flex justify-center md:justify-between gap-3 font-body text-[13px] text-[var(--vibe-color-text-on-primary)]/80">
                      <dt>{group.shortLabel}</dt>
                      <dd className="md:text-right">{group.hours}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="font-body text-[13px] text-[var(--vibe-color-text-on-primary)]/40">
                  Office hours will be displayed here.
                </p>
              )}
            </div>

            {/* Address */}
            <div className="text-center md:text-left md:px-2 lg:px-6">
              <RichTextContent
                path="footer.visit_us_label"
                defaultValue="<p>Visit us</p>"
                as="p"
                className="font-body text-[15px] font-bold text-primary-foreground mb-1"
              />
              <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="font-body text-[13px] text-[var(--vibe-color-text-on-primary)]/80 hover:text-primary-foreground transition-colors block"
                >
                  {addressLink.text || '123 Main Street, City, ST 00000'}
                </a>
              </EditableSlotGroup>
            </div>

            {/* Phone */}
            <div className="text-center md:text-left md:px-2 lg:px-6">
              <RichTextContent
                path="footer.call_us_label"
                defaultValue="<p>Call us</p>"
                as="p"
                className="font-body text-[15px] font-bold text-primary-foreground mb-1"
              />
              {/* `block` on the anchor matches the Address cell above — without it
                  the inline <a> introduces an anonymous line box whose
                  line-height nudges the phone text down a few pixels relative
                  to its sibling cells, breaking the inline row's baseline. */}
              <a
                href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref block font-body text-[13px] text-primary-foreground hover:text-[var(--vibe-color-text-on-primary)]/70 transition-colors"
                style={{ fontFamily: 'var(--vibe-font-body)' }}
              >
                <EditableToken
                  path="footer.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>

            {/* ---- Social Icons (per-icon rounded square in secondary theme color) ----
                Lives in the inline row to the right of Office Hours so
                Logo | Phone | Address | Hours | Socials read as a single
                horizontal strip on desktop. The `.social-icon-ring`
                class (declared in the scoped <style> block below) paints
                the 2px secondary-color border around each icon as a
                SQUARE with a slight 6px corner radius. No caption label
                — the icons stand on their own; the parent cell still
                uses the same `md:px-6` rhythm as the other cells so the
                spacing reads consistently. The block is hidden when no
                social URL is bound so the row doesn't show empty
                squares. The cell is vertically centered via the
                parent's `md:items-stretch` + `flex items-center` here
                so the squares line up with the baseline of the
                neighboring caption + value cells. */}
            {/* Inline-row socials cell. Visible on mobile (< md) and on
                lg+; hidden on md so the tablet-only socials beneath the
                logo take over and Hours/Address/Phone get the full inline
                strip width. */}
            {hasAnySocial && (
              <div className="flex md:hidden lg:flex items-center justify-center lg:justify-start lg:px-6 lg:last:pr-0">
                {socialIconsRow}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---- HR Divider ---- */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ position: 'relative', zIndex: 1 }}>
        <hr className={`border-t ${useWhiteOnPrimary ? 'border-white/20' : 'border-[#1a1a1a]/20'}`} />
      </div>

      {/* ---- Compliance Icons + Legal Nav ---- */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" style={{ position: 'relative', zIndex: 1 }}>
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center justify-center gap-5 text-[var(--vibe-color-text-on-primary)]/60">
            <WheelchairIcon size={28} color="currentColor" />
            <FairHousingIcon size={28} color="currentColor" />
            <PetsAllowedIcon size={28} color="currentColor" />
          </div>

          <nav className="flex flex-wrap items-center justify-center gap-x-1 gap-y-1 font-body text-[13px] text-primary-foreground" aria-label="Legal links">
            <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
              <a href={accessibilityLink.url || '/accessibility'} target={accessibilityLink._target || '_self'} className="hover:text-[var(--vibe-color-text-on-primary)]/60 transition-colors">
                {accessibilityLink.text || 'Accessibility'}
              </a>
            </EditableSlotGroup>
            <span aria-hidden="true" className="text-[var(--vibe-color-text-on-primary)]/40">·</span>
            <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
              <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className="hover:text-[var(--vibe-color-text-on-primary)]/60 transition-colors">
                {privacyPolicyLink.text || 'Privacy Policy'}
              </a>
            </EditableSlotGroup>
            <span aria-hidden="true" className="text-[var(--vibe-color-text-on-primary)]/40">·</span>
            <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
              <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className="hover:text-[var(--vibe-color-text-on-primary)]/60 transition-colors">
                {sitemapLink.text || 'Sitemap'}
              </a>
            </EditableSlotGroup>
          </nav>
        </div>
      </div>

      {/* ---- Bottom Bar (Managed By + Copyright + Powered By + Design By) ----
          Same responsive layout as the base rise Footer — see the long
          comment in the parent for the order/width gymnastics. Kept
          here unchanged so the editor flows stay familiar. */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-6 mt-4" style={{ position: 'relative', zIndex: 1 }}>
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-6 gap-y-3 font-body text-[10px] text-[var(--vibe-color-text-on-primary)]/60">

          <EditableSlotGroup
            groupPath="footer.managed_by_logo"
            definition={runtimeManagedByLogoDef}
            className="order-1 w-full flex justify-center md:w-auto md:justify-start"
          >
            {managedByLogo.image ? (
              <a
                href={managedByLogo.url || '#'}
                target={managedByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.managed_by_text"
                  defaultValue="<p>Professionally Managed by</p>"
                  as="span"
                  className="text-primary-foreground"
                />
                <VibesitesImage
                  url={managedByLogo.image}
                  alt={managedByLogo.alt || 'Property Management Company'}
                  className="h-4 w-auto max-w-[110px] object-contain"
                />
              </a>
            ) : null}
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="footer.powered_by_logo"
            definition={poweredByLogoDef}
            className="order-2 md:order-3"
          >
            <a
              href={poweredByLogo.url || 'https://www.multihub.io/'}
              target={poweredByLogo._target || '_blank'}
              rel="noopener noreferrer"
              className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
            >
              <RichTextContent
                path="footer.powered_by_text"
                defaultValue="<p>Powered by</p>"
                as="span"
                className="text-primary-foreground"
              />
              {useWhiteOnPrimary ? (
                <MultihubLogoMonoWhite height={14} alt={poweredByLogo.alt || 'Multihub'} />
              ) : (
                <MultihubLogoMonoBlack height={14} alt={poweredByLogo.alt || 'Multihub'} />
              )}
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="footer.design_by_logo"
            definition={designByLogoDef}
            className="order-3 md:order-4"
          >
            <a
              href={designByLogo.url || 'https://www.repli360.com/'}
              target={designByLogo._target || '_blank'}
              rel="noopener noreferrer"
              className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
            >
              <RichTextContent
                path="footer.design_by_text"
                defaultValue="<p>Website Design by</p>"
                as="span"
                className="text-primary-foreground"
              />
              <VibesitesImage
                url={useWhiteOnPrimary ? repliWhiteLogoUrl : repliBlackLogoUrl}
                alt={designByLogo.alt || 'Repli Logo'}
                height={16}
                className="h-4 w-auto"
                priority
              />
            </a>
          </EditableSlotGroup>

          <span className="order-4 w-full text-center md:order-2 md:w-auto md:text-left md:mr-auto">
            © {new Date().getFullYear()} All Rights Reserved.
          </span>

        </div>
      </div>

      {/* ---- Scoped styles: social icon rounded squares + RentCafe IQ z-index ----
          The `.social-icon-ring` rule paints the per-icon 2px
          secondary-color SQUARE border with a slight 6px corner radius
          (literal "border around each individual social icon"). Fixed
          28×28 box keeps the icons evenly spaced regardless of which
          platforms are bound. Hover swaps fill to the secondary color
          so the icon "lights up" inside its square without changing
          layout.

          The RentCafe IQ z-index selectors below are a cheap CSS first
          pass limited to the real launcher nodes; the authoritative fix
          lives in the MutationObserver in the effect above (Yardi sets
          inline z-index after mount). */}
      <style>{`
        .social-icon-ring {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 6px;
          border: 2px solid var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-primary);
          transition: color 200ms ease, background-color 200ms ease;
        }
        .social-icon-ring:hover {
          color: var(--vibe-color-secondary);
        }

        .rcc-app-cta-wrap,
        .rcc-app-controls,
        #conversations-webchat-toggle {
          z-index: 120 !important;
        }
      `}</style>
    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.call_us_label': {
      label: 'Call Us Label',
      type: 'richtext',
      defaultValue: '<p>Call us</p>',
    },
    'footer.visit_us_label': {
      label: 'Visit Us Label',
      type: 'richtext',
      defaultValue: '<p>Visit us</p>',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>Office hours</p>',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Professionally Managed by</p>',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered by</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
  },
  slotGroups: {
    'footer.bg_image': bgImageDef,
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.address': addressLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.youtube_link': youtubeLinkDef,
    'footer.program_logo_1': programLogoOneDef,
    'footer.program_logo_2': programLogoTwoDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Footer;
